import { PokeNames as PN } from "types/pokemon/poke-names";
import { ITEM_NAME } from "../items/item-names";
import { PokeType } from "types/pokemon/poke-type";

export enum RaidId {
  GRAND_CINDER_CAVERNS = "grand-cinder-caverns",
  NORTH_MIRIEN_SEWERS = "north-mirien-sewers",
  AVALIS_SOUTHERN_PLAINS = "avalis-southern-plains",
  INVERGROUND_CAVES = "inverground-caves",
  NORTHERN_CAELDRIN_FACTORIES = "northern-caeldrin-factories",
  MIRIEN_SECRET_MANOR = "mirien-secret-manor",
  ETRIOS_BARRIERS = "etrios-barriers",
  CAMPS_OF_INVERGROUND = "camps-of-inverground",
  CINDERFIELDS_HILL = "cinderfields-hill",
  ASTRID_HILLS = "astrid-hills",
  CAELDRIN_GREAT_DOJO = "caeldrin-great-dojo",
  LIZA_PLAINS = "liza-plains",
  ETRIAS_WESTERN_PLAINS = "etrias-western-plains",
  GOLMIR_JUNGLE = "golmir-jungle",
  ANCIENT_ISLAND = "ancient-island",
  MIRIEN_GRAVEYARDS = "mirien-graveyards",
  AVALIST_COAST_SOUTH = "avalist-coast-south",
  SOUTH_MIRIEN_SEWERS = "south-mirien-sewers",
  HAZEL_SECRET_SHRINE = "hazel-secret-shrine",
  ASTRID_WONDER_GARDENS = "astrid-wonder-gardens",
}

const raidNameToIdMap: Record<string, RaidId> = {
  "Grand Cinder Caverns": RaidId.GRAND_CINDER_CAVERNS,
  "North Mirien Sewers": RaidId.NORTH_MIRIEN_SEWERS,
  "Avalis Southern Plains": RaidId.AVALIS_SOUTHERN_PLAINS,
  "Inverground Caves": RaidId.INVERGROUND_CAVES,
  "Northern Caeldrin Factories": RaidId.NORTHERN_CAELDRIN_FACTORIES,
  "Mirien Secret Manor": RaidId.MIRIEN_SECRET_MANOR,
  "Etrios Barriers": RaidId.ETRIOS_BARRIERS,
  "Ancient Island": RaidId.ANCIENT_ISLAND,
  "Hazel Secret Shrine": RaidId.HAZEL_SECRET_SHRINE,
  "Astrid Wonder Garden": RaidId.ASTRID_WONDER_GARDENS,
  "South Mirien Sewers": RaidId.SOUTH_MIRIEN_SEWERS,
  "Avalist Coast South": RaidId.AVALIST_COAST_SOUTH,
  "Mirien Graveyards": RaidId.MIRIEN_GRAVEYARDS,
  "Golmir Jungle": RaidId.GOLMIR_JUNGLE,
  "Camps of Inverground": RaidId.CAMPS_OF_INVERGROUND,
  "Cinderfields Hill": RaidId.CINDERFIELDS_HILL,
  "Astrid Hills": RaidId.ASTRID_HILLS,
  "Caeldrin Great Dojo": RaidId.CAELDRIN_GREAT_DOJO,
  "Liza Plains": RaidId.LIZA_PLAINS,
  "Etrias Western Plains": RaidId.ETRIAS_WESTERN_PLAINS,
};

export const raidNameToId = (name: string): RaidId => {
  const raidId = raidNameToIdMap[name];
  if (!raidId) {
    throw new Error(`Invalid raid name: ${name}`);
  }
  return raidId;
};

export interface RaidConfig {
  id: RaidId;
  name: string;
  description: string;
  bosses: Array<{
    name: PN;
    rarity: "common" | "uncommon" | "rare" | "epic";
    itemDrops?: Array<{
      item_name: ITEM_NAME;
      amount: {
        min: number;
        max: number;
      };
      chance: number;
    }>;
  }>;
  type: PokeType;
  itemDrops: Array<{
    item_name: ITEM_NAME;
    amount: {
      min: number;
      max: number;
    };
    chance: number;
  }>;
  mapIds: string[];
}

/**
 * All available raids in the game.
 * Each raid is themed around a Pokemon type with matching bosses and enemies.
 */
export const RAIDS: RaidConfig[] = [
  {
    id: RaidId.GRAND_CINDER_CAVERNS,
    name: "Grand Cinder Caverns",
    description:
      "Deep within the volcanic mountains of Cinderlands lies a network of lava-filled caverns. Stories tell of powerful Pokemon lives in these caves, and the treasures they guard.",
    bosses: [
      {
        name: PN.CHARIZARD_MEGA_X,
        rarity: "rare",
      },
      {
        name: PN.CAMERUPT_MEGA,
        rarity: "rare",
      },
      {
        name: PN.MAGMORTAR,
        rarity: "uncommon",
        itemDrops: [
          {
            item_name: ITEM_NAME.MAGMARIZER,
            amount: { min: 1, max: 1 },
            chance: 0.05,
          },
        ],
      },
      {
        name: PN.CAMERUPT,
        rarity: "common",
      },
      {
        name: PN.MAGCARGO,
        rarity: "common",
      },
      {
        name: PN.CHARIZARD,
        rarity: "common",
      },
    ],
    type: PokeType.FIRE,
    itemDrops: [
      {
        item_name: ITEM_NAME.FIRE_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 12 },
      (_, i) => `grand-cinder-caverns/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.NORTH_MIRIEN_SEWERS,
    name: "North Mirien Sewers",
    description:
      "Deep beneath the city of North Mirien lies a complex network of sewers. Adventurers have reported seeing strange creatures lurking in the shadows.",
    bosses: [
      {
        name: PN.SNEASLER,
        rarity: "rare",
      },
      {
        name: PN.RATICATE_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.MUK_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.WEEZING_GALAR,
        rarity: "rare",
      },
      {
        name: PN.TOXICROAK,
        rarity: "uncommon",
      },
      {
        name: PN.MUK,
        rarity: "common",
      },
      {
        name: PN.WEEZING,
        rarity: "common",
      },
    ],
    type: PokeType.POISON,
    itemDrops: [
      {
        item_name: ITEM_NAME.DUSK_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 9 },
      (_, i) => `north-mirien-sewers/room (${i + 1})`,
    ),
  },

  // Grass Raid
  {
    id: RaidId.AVALIS_SOUTHERN_PLAINS,
    name: "Avalis Southern Plains",
    description: "In the southern part of Avalis plains...",
    bosses: [
      {
        name: PN.ELECTRODE_HISUI,
        rarity: "rare",
      },
      {
        name: PN.RAICHU_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.MANECTRIC_MEGA,
        rarity: "rare",
      },
      {
        name: PN.ELECTIVIRE,
        rarity: "uncommon",
      },
      {
        name: PN.AMPHAROS,
        rarity: "common",
      },
      {
        name: PN.RAICHU,
        rarity: "common",
      },
    ],
    type: PokeType.GRASS,
    itemDrops: [
      {
        item_name: ITEM_NAME.LEAF_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 34 },
      (_, i) => `avalis-southern-plains/room (${i + 1})`,
    ),
  },

  // Ice Raid
  {
    id: RaidId.INVERGROUND_CAVES,
    name: "Inverground Caves",
    description:
      "The deep frozen caves of Inverground hides a powerful secret.",
    bosses: [
      {
        name: PN.GLALIE_MEGA,
        rarity: "rare",
      },
      {
        name: PN.SANDSLASH_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.LAPRAS,
        rarity: "uncommon",
      },
      {
        name: PN.GLALIE,
        rarity: "common",
      },
      {
        name: PN.FROSLASS,
        rarity: "common",
      },
    ],
    type: PokeType.ICE,
    itemDrops: [
      {
        item_name: ITEM_NAME.ICE_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 34 },
      (_, i) => `inverground-caves/room (${i + 1})`,
    ),
  },

  // Steel/Ground/Rock/Electric Raid
  {
    id: RaidId.NORTHERN_CAELDRIN_FACTORIES,
    name: "Northern Caeldrin Factories",
    description:
      "The northern factories of Caeldrin are home to a powerful secret.",
    bosses: [
      {
        name: PN.STEELIX_MEGA,
        rarity: "rare",
        itemDrops: [
          {
            item_name: ITEM_NAME.METAL_COAT,
            amount: { min: 1, max: 1 },
            chance: 0.08,
          },
        ],
      },
      {
        name: PN.DUGTRIO_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.GOLEM_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.CLODSIRE,
        rarity: "rare",
      },
      {
        name: PN.STEELIX,
        rarity: "uncommon",
        itemDrops: [
          {
            item_name: ITEM_NAME.METAL_COAT,
            amount: { min: 1, max: 1 },
            chance: 0.05,
          },
        ],
      },
      {
        name: PN.MAGNEZONE,
        rarity: "uncommon",
        itemDrops: [
          {
            item_name: ITEM_NAME.MAGNET,
            amount: { min: 1, max: 1 },
            chance: 0.05,
          },
        ],
      },
      {
        name: PN.EXCADRILL,
        rarity: "common",
      },
      {
        name: PN.GOLEM,
        rarity: "common",
      },
    ],
    type: PokeType.STEEL,
    itemDrops: [
      {
        item_name: ITEM_NAME.METAL_COAT,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from({ length: 5 }, (_, i) => `steel/steel (${i + 1})`),
  },

  // dark/ghost Raid
  {
    id: RaidId.MIRIEN_SECRET_MANOR,
    name: "Mirien Secret Manor",
    description: "The secret manor of Mirien is home to a powerful secret.",
    bosses: [
      {
        name: PN.ABSOL_MEGA,
        rarity: "rare",
      },
      {
        name: PN.PERSIAN_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.OBSTAGOON,
        rarity: "rare",
      },
      {
        name: PN.CURSOLA,
        rarity: "rare",
      },
      {
        name: PN.DUSKNOIR,
        rarity: "uncommon",
        itemDrops: [
          {
            item_name: ITEM_NAME.REAPER_CLOTH,
            amount: { min: 1, max: 1 },
            chance: 0.05,
          },
        ],
      },
      {
        name: PN.ABSOL,
        rarity: "common",
      },
      {
        name: PN.MIGHTYENA,
        rarity: "common",
      },
    ],
    type: PokeType.DARK,
    itemDrops: [
      {
        item_name: ITEM_NAME.DUSK_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 34 },
      (_, i) => `mirien-secret-manor/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.ETRIOS_BARRIERS,
    name: "Etrios Barriers",
    description: "The barriers of Etrios are home to a powerful secret.",
    bosses: [
      {
        name: PN.TYRANITAR_MEGA,
        rarity: "rare",
      },
      {
        name: PN.ARCANINE_HISUI,
        rarity: "rare",
      },
      {
        name: PN.RHYPERIOR,
        rarity: "uncommon",
        itemDrops: [
          {
            item_name: ITEM_NAME.PROTECTOR,
            amount: { min: 1, max: 1 },
            chance: 0.05,
          },
        ],
      },
      {
        name: PN.TYRANTRUM,
        rarity: "common",
      },
      {
        name: PN.TYRANITAR,
        rarity: "common",
      },
    ],
    type: PokeType.ROCK,
    itemDrops: [
      {
        item_name: ITEM_NAME.DAMP_ROCK,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 18 },
      (_, i) => `etrios-barriers/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.ANCIENT_ISLAND,
    name: "Ancient Island",
    description: "The island of Ancient is home to a powerful secret.",
    bosses: [
      {
        name: PN.AERODACTYL_MEGA,
        rarity: "rare",
      },
      {
        name: PN.AERODACTYL,
        rarity: "uncommon",
      },
      {
        name: PN.ARMALDO,
        rarity: "common",
      },
      {
        name: PN.CARRACOSTA,
        rarity: "common",
      },
      {
        name: PN.CRADILY,
        rarity: "common",
      },
    ],
    type: PokeType.ROCK,
    itemDrops: [],
    mapIds: Array.from(
      { length: 21 },
      (_, i) => `ancient-island/room (${i + 1})`,
    ),
  },

  // Psychic Raid
  {
    id: RaidId.HAZEL_SECRET_SHRINE,
    name: "Hazel Secret Shrine",
    description: "The secret shrine of Hazel is home to a powerful secret.",
    bosses: [
      {
        name: PN.ZOROARK_HISUI,
        rarity: "rare",
      },
      {
        name: PN.ALAKAZAM_MEGA,
        rarity: "rare",
      },
      {
        name: PN.BANETTE_MEGA,
        rarity: "rare",
      },
      {
        name: PN.METAGROSS,
        rarity: "uncommon",
        itemDrops: [
          {
            item_name: ITEM_NAME.METAL_COAT,
            amount: { min: 1, max: 1 },
            chance: 0.05,
          },
        ],
      },
      {
        name: PN.ALAKAZAM,
        rarity: "common",
      },
      {
        name: PN.ZOROARK,
        rarity: "common",
      },
      {
        name: PN.BANETTE,
        rarity: "common",
      },
    ],
    type: PokeType.PSYCHIC,
    itemDrops: [
      {
        item_name: ITEM_NAME.DAWN_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 6 },
      (_, i) => `hazel-secret-shrine/room (${i + 1})`,
    ),
  },

  // Bug Raid
  {
    id: RaidId.ASTRID_WONDER_GARDENS,
    name: "Astrid Wonder Garden",
    description: "The wonder garden of Astrid is home to a powerful secret.",
    bosses: [
      {
        name: PN.GARDEVOIR_MEGA,
        rarity: "rare",
      },
      {
        name: PN.RAPIDASH_GALAR,
        rarity: "rare",
      },
      {
        name: PN.MAWILE_MEGA,
        rarity: "rare",
      },
      {
        name: PN.MAWILE,
        rarity: "common",
      },
      {
        name: PN.GARDEVOIR,
        rarity: "common",
      },
    ],
    type: PokeType.FAIRY,
    itemDrops: [],
    mapIds: Array.from(
      { length: 17 },
      (_, i) => `astrid-wonder-gardens/garden (${i + 1})`,
    ),
  },

  {
    id: RaidId.SOUTH_MIRIEN_SEWERS,
    name: "South Mirien Sewers",
    description:
      "Deep beneath the city of South Mirien lies a complex network of sewers. Adventurers have reported seeing strange creatures lurking in the shadows.",
    bosses: [
      {
        name: PN.SLOWKING_GALAR,
        rarity: "rare",
      },
      {
        name: PN.OVERQWIL,
        rarity: "rare",
      },
      {
        name: PN.TENTACRUEL,
        rarity: "common",
      },
    ],
    type: PokeType.POISON,
    itemDrops: [
      {
        item_name: ITEM_NAME.DUSK_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.01,
      },
    ],
    mapIds: Array.from(
      { length: 15 },
      (_, i) => `south-mirien-sewers/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.AVALIST_COAST_SOUTH,
    name: "Avalist Coast South",
    description:
      "A decrepit mansion where Ghost-type Pokemon have made their home. Shadows move on their own, and the air is thick with supernatural energy.",
    bosses: [
      {
        name: PN.SAMUROTT_HISUI,
        rarity: "rare",
      },
      {
        name: PN.SLOWBRO_MEGA,
        rarity: "rare",
      },
      {
        name: PN.SAMUROTT,
        rarity: "rare",
      },
      {
        name: PN.SLOWKING,
        rarity: "rare",
      },
    ],
    type: PokeType.WATER,
    itemDrops: [
      {
        item_name: ITEM_NAME.KINGS_ROCK,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 15 },
      (_, i) => `avalist-coast-south/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.MIRIEN_GRAVEYARDS,
    name: "Mirien Graveyards",
    description:
      "A legendary mountain where Dragon-type Pokemon reign supreme. Only the bravest trainers dare to climb to the summit.",
    bosses: [
      {
        name: PN.MAROWAK_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.GENGAR_MEGA,
        rarity: "rare",
      },
      {
        name: PN.SABLEYE_MEGA,
        rarity: "rare",
      },
      {
        name: PN.RUNERIGUS,
        rarity: "rare",
      },
      {
        name: PN.COFAGRIGUS,
        rarity: "common",
      },
      {
        name: PN.GENGAR,
        rarity: "common",
      },
    ],
    type: PokeType.GHOST,
    itemDrops: [],
    mapIds: Array.from(
      { length: 22 },
      (_, i) => `mirien-graveyards/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.GOLMIR_JUNGLE,
    name: "Golmir Jungle",
    description:
      "A dimension of eternal darkness where Dark-type Pokemon lurk. Light barely penetrates this sinister domain.",
    bosses: [
      {
        name: PN.EXEGGUTOR_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.EXEGGUTOR,
        rarity: "common",
      },
    ],
    type: PokeType.GRASS,
    itemDrops: [
      {
        item_name: ITEM_NAME.LEAF_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 10 },
      (_, i) => `golmir-jungle/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.CAMPS_OF_INVERGROUND,
    name: "Camps of Inverground",
    description:
      "A massive metallic stronghold built by Steel-type Pokemon. Its defenses are nearly impenetrable.",
    bosses: [
      {
        name: PN.NINETALES_ALOLA,
        rarity: "rare",
      },
      {
        name: PN.MR_RIME,
        rarity: "rare",
      },
      {
        name: PN.ABOMASNOW,
        rarity: "common",
      },
      {
        name: PN.BEARTIC,
        rarity: "common",
      },
    ],
    type: PokeType.ICE,
    itemDrops: [
      {
        item_name: ITEM_NAME.ICE_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 14 },
      (_, i) => `camps-of-inverground/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.CINDERFIELDS_HILL,
    name: "Cinderfields Hill",
    description:
      "A magical forest where Fairy-type Pokemon frolic among the flowers. Don't be fooled by their cute appearance - they're formidable foes.",
    bosses: [
      {
        name: PN.HOUNDOOM_MEGA,
        rarity: "rare",
      },
      {
        name: PN.TYPHLOSION_HISUI,
        rarity: "rare",
      },
      {
        name: PN.TYPHLOSION,
        rarity: "common",
      },
      {
        name: PN.HOUNDOOM,
        rarity: "common",
      },
    ],
    type: PokeType.FIRE,
    itemDrops: [
      {
        item_name: ITEM_NAME.FIRE_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 17 },
      (_, i) => `cinderfields-hill/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.ASTRID_HILLS,
    name: "Astrid Hills",
    description:
      "Vast open plains where Normal-type Pokemon roam freely. Simple in appearance, but don't underestimate their versatility.",
    bosses: [
      {
        name: PN.ALTARIA_MEGA,
        rarity: "rare",
      },
      {
        name: PN.BRAVIARY_HISUI,
        rarity: "rare",
      },
      {
        name: PN.BRAVIARY,
        rarity: "common",
      },
      {
        name: PN.ALTARIA,
        rarity: "common",
      },
    ],
    type: PokeType.FLYING,
    itemDrops: [
      {
        item_name: ITEM_NAME.SHINY_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 22 },
      (_, i) => `astrid-hills/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.CAELDRIN_GREAT_DOJO,
    name: "Caeldrin Great Dojo",
    description:
      "A massive dojo where Fighting-type Pokemon train. Only the strongest trainers dare to enter.",
    bosses: [
      {
        name: PN.GALLADE_MEGA,
        rarity: "rare",
      },
      {
        name: PN.MEDICHAM_MEGA,
        rarity: "rare",
      },
      {
        name: PN.LUCARIO_MEGA,
        rarity: "rare",
      },
      {
        name: PN.SIRFETCHD,
        rarity: "rare",
      },
      {
        name: PN.GALLADE,
        rarity: "common",
      },
      {
        name: PN.LUCARIO,
        rarity: "common",
      },
      {
        name: PN.MEDICHAM,
        rarity: "common",
      },
    ],
    type: PokeType.FIGHTING,
    itemDrops: [
      {
        item_name: ITEM_NAME.DUBIOUS_DISC,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 22 },
      (_, i) => `caeldrin-great-dojo/room (${i + 1})`,
    ),
  },

  {
    id: RaidId.LIZA_PLAINS,
    name: "Liza Plains",
    description:
      "A vast plains where Normal-type Pokemon roam freely. Simple in appearance, but don't underestimate their versatility.",
    bosses: [
      {
        name: PN.DECIDUEYE_HISUI,
        rarity: "rare",
      },
      {
        name: PN.LILLIGANT_HISUI,
        rarity: "rare",
      },
      {
        name: PN.DECIDUEYE,
        rarity: "common",
      },
      {
        name: PN.LILLIGANT,
        rarity: "common",
      },
    ],
    type: PokeType.GRASS,
    itemDrops: [
      {
        item_name: ITEM_NAME.LEAF_STONE,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from({ length: 22 }, (_, i) => `liza-plains/room (${i + 1})`),
  },

  {
    id: RaidId.ETRIAS_WESTERN_PLAINS,
    name: "Etrias Western Plains",
    description:
      "A vast plains where Normal-type Pokemon roam freely. Simple in appearance, but don't underestimate their versatility.",
    bosses: [
      {
        name: PN.KANGASKHAN_MEGA,
        rarity: "rare",
      },
      {
        name: PN.LOPUNNY_MEGA,
        rarity: "rare",
      },
      {
        name: PN.TAUROS_PALDEA_COMBAT_BREED,
        rarity: "rare",
      },
      {
        name: PN.LOPUNNY,
        rarity: "common",
      },
      {
        name: PN.KANGASKHAN,
        rarity: "common",
      },
    ],
    type: PokeType.NORMAL,
    itemDrops: [
      {
        item_name: ITEM_NAME.DUBIOUS_DISC,
        amount: { min: 1, max: 1 },
        chance: 0.05,
      },
    ],
    mapIds: Array.from(
      { length: 22 },
      (_, i) => `etrias-western-plains/room (${i + 1})`,
    ),
  },
];

/**
 * Gets a raid configuration by name.
 */
export function getRaidByName(name: string): RaidConfig | undefined {
  return RAIDS.find((raid) => raid.name === name);
}

/**
 * Gets a raid configuration by id.
 */
export function getRaidById(id: RaidId): RaidConfig {
  const raid = RAIDS.find((raid) => raid.id === id);
  if (!raid) {
    throw new Error(`Invalid raid id: ${id}`);
  }
  return raid;
}
